import type { CSSProperties } from "react";
import type { SchemeTokens } from "@shared/types/foyer";
import { careManifest } from "@shared/schemes/manifests";

/**
 * The shared look of every call window (VC3-9).
 *
 * ── Why this file exists ────────────────────────────────────────────────────
 *
 * The call flow spans five surfaces in three files: the incoming ring and its
 * auto-answer announce variant (IncomingCallOverlay), the connecting veil
 * (IncomingCallOverlay + CallMediaSurface + CareScheme), the in-call chrome
 * (CallMediaSurface), and the outbound/ended cards (CareScheme). Until now each
 * one carried its own hex codes, its own pill geometry and its own type sizes,
 * so the same call could change typeface, radius and contrast three times on the
 * way through. That is what "off theme" meant.
 *
 * ── The decision this encodes ───────────────────────────────────────────────
 *
 * The ring was deliberately built NOT to borrow scheme tokens, because it paints
 * over any scheme - the slideshow, the whiteboard, the radio, live TV. That
 * reasoning conflated two different things:
 *
 *   • what is BEHIND the window (arbitrary, and the reason the scrim is opaque),
 *   • which PRODUCT the window belongs to (always Hemvist - SchemeRouter only
 *     subscribes to a room's call session when the scheme key is `care`, so a
 *     call window has never once appeared on a hospitality TV).
 *
 * So the scrim stays opaque and universal - that is what guarantees legibility
 * over anything - and the CARD on top of it is the care scheme's own warm paper,
 * green and Figtree. A light card on an opaque dark scrim is high-contrast in
 * both directions, which is exactly what the ring needed anyway.
 *
 * Tokens are passed in rather than read from context on purpose: the ring is
 * mounted by SchemeRouter OUTSIDE the scheme provider (that is what lets it
 * paint above every screen), so it cannot call `useScheme()`. SchemeRouter hands
 * it the resolved tokens instead, which means operator colour overrides still
 * flow through. Called with nothing, it falls back to the care manifest
 * defaults - the palette every care premise starts from.
 *
 * ── What deliberately does NOT follow the scheme ────────────────────────────
 *
 *   • `scrim` - opaque by contract, so a ring is readable over a bright photo.
 *   • `video` / `onVideo` - the media plane is near-black because it frames live
 *     video; flashing warm paper up before the first frame would be worse.
 *   • `cameraRed` - the live-camera privacy chip (design §4.4) is louder than
 *     anything in the care palette on purpose. It is an affordance, not decor.
 */
export interface CallTheme {
  /** Opaque backdrop. Its job is legibility over arbitrary content, not brand. */
  scrim: string;
  paper: string;
  surface: string;
  surface2: string;
  hair: string;
  hairSoft: string;
  ink: string;
  inkSoft: string;
  inkMuted: string;
  green: string;
  greenDeep: string;
  greenSoft: string;
  greenTint: string;
  /** Care's warning red - used for a failure eyebrow, never for a hang-up key. */
  alert: string;
  /** The video plane and the type that sits on it. */
  video: string;
  onVideo: string;
  /** The live-camera chip. Deliberately louder than the care palette (§4.4). */
  cameraRed: string;
  sans: string;
  mono: string;
}

/**
 * One type scale for the whole call flow, so a resident meets the same sizes
 * whether the call is coming in or going out. Values are TV pixels at 1080p
 * (the schemes render at a fixed 1920×1080 stage), read from a chair.
 */
export const CALL_TYPE = {
  /** Mono, uppercase, tracked - the care scheme's eyebrow voice. */
  eyebrow: 24,
  /** The caller's name on the ring: the loudest thing in the product. */
  name: 84,
  /** The ended/outbound card heading. */
  title: 68,
  relation: 38,
  body: 34,
  hint: 28,
  /** Button label. */
  action: 42,
  /** Key cap inside a button. */
  cap: 24,
  /** Pills laid over live video. */
  chrome: 34,
} as const;

const D = careManifest.defaultTokens;

/** Resolve the call palette from a scheme's tokens (care manifest defaults). */
export function callTheme(tokens?: SchemeTokens): CallTheme {
  const palette = { ...D.palette, ...tokens?.palette };
  const extras = { ...D.extras, ...tokens?.extras };
  const fonts = { ...D.fonts, ...tokens?.fonts };
  return {
    // Deep, warm and near-opaque: dark enough that a bright slideshow behind it
    // cannot compete with the card, warm enough not to read as a system error.
    scrim: "rgba(12,17,13,0.94)",
    paper: palette.bg,
    surface: extras.surface ?? "#FFFFFF",
    surface2: extras.surface2 ?? "#FAF8F2",
    hair: extras.hair ?? "#D8D4C6",
    hairSoft: palette.rule ?? "#E4E1D4",
    ink: palette.ink,
    inkSoft: palette.inkDim ?? "#5F665C",
    inkMuted: extras.inkMuted ?? "#8E948A",
    green: palette.accent,
    greenDeep: palette.accent2 ?? "#1F3D2A",
    greenSoft: extras.gSoft ?? "#A8C3A4",
    greenTint: extras.gTint ?? "#E3ECD8",
    alert: "#B23B3B",
    video: "#0B0D0A",
    onVideo: "#FFFFFF",
    cameraRed: "#C8102E",
    sans: fonts.sans ?? '"Figtree", system-ui, sans-serif',
    mono: fonts.mono ?? '"JetBrains Mono", ui-monospace, monospace',
  };
}

/**
 * The card every non-video call window shares - the ring, the announce screen,
 * the "could not connect" notice, and (by matching values) CareScheme's own
 * outbound/ended overlay. Incoming and outgoing should read as siblings; before
 * VC3-9 they were a black slab and a white card.
 */
export function callCard(t: CallTheme): CSSProperties {
  return {
    width: "100%",
    maxWidth: 1200,
    background: t.surface,
    border: `1px solid ${t.hairSoft}`,
    borderRadius: 44,
    padding: "72px 88px",
    textAlign: "center",
    // A soft lift rather than a hard drop shadow: the scrim already separates it.
    boxShadow: "0 60px 140px -50px rgba(0,0,0,0.65)",
  };
}

/** Mono, uppercase, tracked. The care scheme's eyebrow, at overlay size. */
export function callEyebrow(t: CallTheme, tone: "muted" | "alert" = "muted") {
  return {
    fontFamily: t.mono,
    fontSize: CALL_TYPE.eyebrow,
    letterSpacing: "0.18em",
    textTransform: "uppercase" as const,
    color: tone === "alert" ? t.alert : t.inkMuted,
    // 1.4 keeps descenders clear of any clipping ancestor - see the
    // tv-tight-line-height-clipping note.
    lineHeight: 1.4,
    // An eyebrow is always followed by the thing it labels; carrying the gap
    // here is what keeps the ring, the announce screen and the failure notice
    // sitting on the same rhythm.
    marginBottom: 40,
  };
}

/**
 * A pill laid over live video (the name label, the "end the call" hint).
 * One geometry for all of them, so the in-call chrome reads as one system.
 */
export function videoPill(t: CallTheme): CSSProperties {
  return {
    padding: "16px 34px",
    borderRadius: 999,
    background: "rgba(11,13,10,0.62)",
    border: "1px solid rgba(255,255,255,0.16)",
    color: t.onVideo,
    fontFamily: t.sans,
    fontWeight: 600,
    backdropFilter: "blur(8px)",
    lineHeight: 1.3,
  };
}
